import React from 'react'
import { ArrowRightIcon, DocumentCheckIcon } from '@heroicons/react/24/outline'

import { Badge } from '../../../../components/ui'
import { cn } from '../../../../shared/lib/cn'
import {
  getWorkflowSystemIcon,
  runStatusLabels,
  runStatusVariants,
  workflowStatusLabels,
  workflowStepVariants,
  type WorkflowRun,
  type WorkflowStep,
  type WorkflowStepState,
} from '../../domain/contractWorkflow'

export function WorkflowTracker({ steps }: { steps: WorkflowStep[] }) {
  const activeStep = steps.find(step => step.state === 'active')
  const blockedStep = steps.find(step => step.state === 'blocked')
  const completedStep = [...steps].reverse().find(step => step.state === 'complete')
  const currentStep = blockedStep || activeStep || completedStep
  const hasStarted = steps.some(step => step.state !== 'pending')
  const isComplete = steps.length > 0 && steps.every(step => step.state === 'complete')
  const signatureStep = steps.find(step => step.id === 'sow-signature-received')
  const downstreamSteps = steps.filter(step => ['hubspot-updated', 'revenue-spreadsheets-updated', 'operating-updated'].includes(step.id))
  const extractionState: WorkflowStepState =
    downstreamSteps.some(step => step.state === 'active' || step.state === 'complete')
      ? 'complete'
      : signatureStep?.state === 'complete'
        ? 'active'
        : blockedStep
          ? 'blocked'
          : 'pending'

  const FlowArrow = ({ vertical = false }: { vertical?: boolean }) => (
    <div
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center text-gray-300 dark:text-gray-600',
        vertical ? 'h-8 rotate-90 xl:h-auto xl:rotate-0' : 'h-8 xl:h-auto',
      )}
    >
      <ArrowRightIcon className="h-6 w-6" />
    </div>
  )

  const FlowNode = ({
    title,
    detail,
    state,
    icon: Icon,
    accent = false,
  }: {
    title: string
    detail: string
    state: WorkflowStepState
    icon: React.ComponentType<{ className?: string }>
    accent?: boolean
  }) => (
    <div
      className={cn(
        'min-w-0 rounded-lg border p-4 transition-colors',
        state === 'blocked'
          ? 'border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30'
          : state === 'complete'
            ? 'border-emerald-300 bg-emerald-50 shadow-sm ring-1 ring-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/30 dark:ring-emerald-900/40'
            : accent
              ? 'border-eliza-red/30 bg-eliza-red/5 dark:border-eliza-red/40 dark:bg-eliza-red/10'
              : 'border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border',
              state === 'blocked'
                ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300'
                : accent
                  ? 'border-eliza-red/30 bg-white text-eliza-red dark:bg-dark-surface'
                  : state === 'complete'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
                    : 'border-gray-200 bg-gray-50 text-gray-500 dark:border-dark-border dark:bg-dark-bg dark:text-gray-300',
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">{title}</h3>
            <p className="mt-1 line-clamp-3 break-words text-xs text-gray-500 dark:text-gray-400">{detail}</p>
          </div>
        </div>
        <Badge variant={workflowStepVariants[state]} className="rounded-md">
          {workflowStatusLabels[state]}
        </Badge>
      </div>
    </div>
  )

  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card dark:border-dark-border/30 dark:bg-dark-surface">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-subtitle text-xl text-charcoal dark:text-gray-100">SOW workflow tracker</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            One approved SOW becomes the source packet for HubSpot, Operating, and revenue spreadsheets.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={isComplete ? 'success' : blockedStep ? 'danger' : hasStarted ? 'info' : 'secondary'} className="rounded-md">
            {isComplete ? 'Workflow complete' : blockedStep ? 'Needs attention' : hasStarted ? 'Workflow in progress' : 'Waiting for SOW'}
          </Badge>
          {currentStep && (
            <Badge variant="secondary" className="rounded-md">
              Now: {currentStep.label}
            </Badge>
          )}
        </div>
      </div>

      <div className="mt-5 grid min-w-0 items-center gap-3 xl:grid-cols-[minmax(0,1fr)_32px_minmax(0,1fr)_32px_minmax(0,1.35fr)]">
        <FlowNode
          title={signatureStep?.label || 'SOW Signature Received'}
          detail={signatureStep?.detail || 'Waiting for signature email'}
          state={signatureStep?.state || 'pending'}
          icon={DocumentCheckIcon}
        />
        <FlowArrow vertical />
        <FlowNode
          title="SOW Extraction"
          detail={extractionState === 'complete' ? 'Approval fields extracted from PDF' : signatureStep?.state === 'complete' ? 'Ready to parse signed SOW PDF' : 'Waiting on signature'}
          state={extractionState}
          icon={DocumentCheckIcon}
          accent
        />

        <FlowArrow vertical />
        <div className="relative grid min-w-0 gap-3">
          <div className="absolute bottom-0 left-3 top-0 hidden w-0.5 bg-gray-200 dark:bg-dark-border xl:block" aria-hidden="true" />
          {downstreamSteps.map(step => {
            const SystemIcon = getWorkflowSystemIcon(step)
            return (
              <div key={step.id} className="relative min-w-0 xl:pl-7">
                <div className="absolute left-3 top-1/2 hidden h-0.5 w-5 bg-gray-200 dark:bg-dark-border xl:block" aria-hidden="true" />
                <FlowNode
                  title={step.label}
                  detail={step.detail}
                  state={step.state}
                  icon={SystemIcon}
                />
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export function RunTracePanel({
  runs,
  selectedRunId,
  onSelectRun,
}: {
  runs: WorkflowRun[]
  selectedRunId?: string
  onSelectRun: (runId: string) => void
}) {
  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card dark:border-dark-border/30 dark:bg-dark-surface">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-subtitle text-xl text-charcoal dark:text-gray-100">Run traces</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Recent SOW intake and handoff traces with the first failure point called out.
          </p>
        </div>
        <Badge variant="secondary" className="rounded-md">Trace preview</Badge>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-dark-border">
        <div className="divide-y divide-gray-100 dark:divide-dark-border/70">
          {runs.length === 0 && (
            <div className="bg-white p-6 text-sm text-gray-500 dark:bg-dark-surface dark:text-gray-400">
              No SOW workflow runs yet. Scan Gmail and parse a signed Statement of Work to start tracking.
            </div>
          )}
          {runs.map(run => {
            const failedStep = run.steps.find(step => step.state === 'blocked')
            const activeStep = run.steps.find(step => step.state === 'active')
            const focusStep = failedStep || activeStep || run.steps[run.steps.length - 1]
            const isSelected = run.id === selectedRunId
            return (
              <button
                key={run.id}
                type="button"
                onClick={() => onSelectRun(run.id)}
                className={cn(
                  'grid w-full gap-4 bg-white p-4 text-left transition-colors dark:bg-dark-surface xl:grid-cols-[minmax(220px,0.5fr)_minmax(0,1.5fr)]',
                  isSelected && 'bg-gray-50 ring-2 ring-eliza-red/30 dark:bg-dark-surface-2',
                  !isSelected && 'hover:bg-gray-50 dark:hover:bg-dark-surface-2',
                )}
                aria-pressed={isSelected}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={runStatusVariants[run.status]} className="rounded-md">
                      {runStatusLabels[run.status]}
                    </Badge>
                    {isSelected && <Badge variant="default" className="rounded-md">Selected</Badge>}
                    <span className="text-xs text-gray-500 dark:text-gray-400">{run.startedAt}</span>
                  </div>
                  <p className="mt-2 truncate text-sm font-semibold text-charcoal dark:text-gray-100">{run.label}</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {focusStep?.label}: {focusStep?.detail}
                  </p>
                  {failedStep?.failure && (
                    <p className="mt-2 text-xs leading-5 text-rose-700 dark:text-rose-300">{failedStep.failure}</p>
                  )}
                </div>
                <div className="grid gap-2 lg:grid-cols-5">
                  {run.traces.map(trace => (
                    <div key={`${run.id}-${trace.id}`} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-bg">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-xs font-medium text-charcoal dark:text-gray-100">{trace.label}</p>
                        <span
                          className={cn(
                            'h-2.5 w-2.5 shrink-0 rounded-full',
                            trace.status === 'complete' && 'bg-emerald-500',
                            trace.status === 'active' && 'bg-sky-500',
                            trace.status === 'blocked' && 'bg-rose-500',
                            trace.status === 'pending' && 'bg-gray-300 dark:bg-gray-600',
                          )}
                        />
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                        <span className="truncate">{trace.source}</span>
                        <ArrowRightIcon className="h-3 w-3 shrink-0" />
                        <span className="truncate">{trace.target}</span>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{trace.timestamp}</p>
                      <p className="mt-1 line-clamp-2 text-xs leading-5 text-gray-600 dark:text-gray-300">{trace.detail}</p>
                    </div>
                  ))}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </section>
  )
}
